import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Divider, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';

import { usePostData } from '@/helpers/hooks';
import { isValidEmail } from '@/helpers/utils';

interface Props {
  handleForgotPWClick: (b: boolean) => void;
}

const ResetPasswordForm: React.FC<Props> = ({ handleForgotPWClick }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [resetEmailAddr, setResetEmailAddr] = useState<string>('');
  const [resetEmailErr, setResetEmailErr] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const resetPasswordMutation = usePostData<unknown, unknown, { email: string }, string>({
    url: `/Login/RequestResetPassword`,
    options: {
      onSuccess: () => {
        setIsSubmitted(true);
      },
      onError: () => {
        enqueueSnackbar('An error happened, please try again', {
          variant: 'error'
        });
      }
    }
  });
  const handleResetEmail = async () => {
    if (!resetEmailAddr) {
      setResetEmailErr('Email address is required');
      return;
    }
    if (!isValidEmail(resetEmailAddr)) {
      setResetEmailErr('Email address is not valid');
      return;
    }
    await resetPasswordMutation.mutateAsync({ email: resetEmailAddr });
  };
  return (
    <>
      {!isSubmitted ? (
        <div className='space-y-4'>
          <h1 className='mb-2 text-base font-semibold text-[#111111]'>Forgot your password?</h1>
          <Typography>Enter the email address for your account so we can send you reset instructions</Typography>
          <TextField
            required
            size={'small'}
            fullWidth
            label='Email address'
            value={resetEmailAddr}
            variant='filled'
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setResetEmailErr('');
              setResetEmailAddr(event.target.value);
            }}
            sx={{
              '& .MuiInputBase-root': {
                backgroundColor: 'transparent',
                borderRadius: '8px',
                '& fieldset': {
                  border: '1px solid white'
                }
              },
              marginTop: 2,
              marginBottom: 2
            }}
            error={!!resetEmailErr}
            helperText={resetEmailErr}
          ></TextField>
          <div className='flex justify-end'>
            <LoadingButton
              className='h-[30px] !rounded-[0px] !bg-[#0067B8] text-white'
              type='button'
              onClick={handleResetEmail}
              variant='contained'
              loading={resetPasswordMutation.isPending}
            >
              Send Reset Instructions
            </LoadingButton>
          </div>

          <div className='flex items-center justify-center'>
            <Typography>Back to</Typography>
            <Button sx={{ textTransform: 'none', padding: 0 }} onClick={() => handleForgotPWClick(false)}>
              Sign in
            </Button>
          </div>
        </div>
      ) : (
        <div className='space-y-4'>
          <h1 className='mb-2 text-base font-semibold text-[#111111]'>Forgot your password?</h1>
          <Typography>Enter the email address for your account so we can send you reset instructions</Typography>
          <Divider></Divider>
          <Box
            sx={{
              backgroundColor: theme => theme.palette.backgroundBrown.dark,
              borderRadius: '10px',
              padding: 1,
              marginY: 2
            }}
          >
            <Typography
              sx={{
                color: 'success.main',
                fontSize: 14,
                fontWeight: 400
              }}
            >
              Thank you. if an account exists with this email address, you should receive an email to reset your
              password. Otherwise, please contact support.
            </Typography>
          </Box>
        </div>
      )}
    </>
  );
};
export default ResetPasswordForm;
